<template>
    <MPModal
        v-model="isOpen"
        variant="center"
        size="3xl"
    >
        <template #header>
            <div
                v-if="viewerFile"
                class="flex min-w-0 flex-1 items-center gap-2 pr-2"
            >
                <div class="flex min-w-0 flex-col">
                    <DialogTitle
                        as="h3"
                        class="truncate text-lg font-medium"
                    >
                        {{ getLabel(viewerFile) }}
                    </DialogTitle>
                    <span
                        class="text-muval-gray-2 text-sm tabular-nums"
                        data-test="file-viewer-position"
                    >
                        <template v-if="viewerFile.created_at"
                            >{{ formatUploadedAt(viewerFile.created_at) }} · </template
                        >{{ viewerIndex + 1 }} / {{ Math.max(total ?? 0, files.length) }}
                    </span>
                </div>
                <div class="ml-auto flex shrink-0 items-center gap-1">
                    <MuvalButton
                        v-if="canDownload(viewerFile)"
                        tooltip="Download"
                        aria-label="Download"
                        icon="system-download"
                        tertiary
                        small
                        type="button"
                        data-test="file-viewer-download"
                        @click="emit('download', viewerFile)"
                    />
                    <MuvalButton
                        tooltip="Open in new tab"
                        aria-label="Open in new tab"
                        icon="system-arrow-outward"
                        tertiary
                        small
                        type="button"
                        data-test="file-viewer-open"
                        @click="emit('open', viewerFile)"
                    />
                </div>
            </div>
            <DialogTitle
                v-else
                as="h3"
                class="text-lg font-medium"
            >
                Files
            </DialogTitle>
        </template>

        <div
            v-if="viewerFile"
            class="md:rounded-b-muval-2 -mx-4 -my-4 h-[65vh] overflow-hidden sm:-mx-6"
            data-test="file-viewer-body"
        >
            <FileViewer
                :files="files"
                :start-index="viewerIndex"
                :url-for="previewUrlFor"
                :unavailable-ids="hiddenIds"
                :get-label="getLabel"
                :can-download="canDownload"
                @update:index="(index: number) => (viewerIndex = index)"
                @need-url="requestPreviewUrl"
                @preview-error="onPreviewError"
                @preview-blocked="onPreviewBlocked"
                @open="(file: Record<string, any>) => emit('open', file)"
            />
        </div>
    </MPModal>
</template>

<script setup lang="ts">
import { DialogTitle } from 'reka-ui';
import MuvalButton from '@buttons/MuvalButton.vue';
import FileViewer from '@components/files/FileViewer.vue';
import MPModal from '@components/modal/MPModal.vue';
import useDayJs from '@plugins/useDayJs';
import { useUserStore } from '@store/user';
import { getFileDisplayName, previewKind } from '@utilities/fileDisplay';

/**
 * A file list's viewer, opened at a file with `openAt` and paging through the
 * whole list in place. Shows the caller's own array; the caller supplies
 * preview links.
 */

type FileRecord = Record<string, any>;
type FileId = string | number;

const props = withDefaults(
    defineProps<{
        files: FileRecord[];
        getLabel?: (file: FileRecord) => string;
        canDownload?: (file: FileRecord) => boolean;
        /** A short-lived link the viewer can show the file from in place. */
        resolvePreviewUrl: (file: FileRecord) => Promise<string>;
        /** How many files the whole list holds, when `files` is only the pages loaded so far. */
        total?: number;
    }>(),
    {
        getLabel: getFileDisplayName,
        canDownload: () => true,
    },
);

const emit = defineEmits<{
    open: [file: FileRecord];
    download: [file: FileRecord];
    /** An image from the list failed to load; refetch the list for fresh URLs. */
    refresh: [];
    /** Paging is near the last file loaded; a paginated list should load its next page. */
    'load-more': [];
}>();

const isOpen = defineModel<boolean>({ default: false });

const dayjs = useDayJs();
const userStore = useUserStore();

const viewerIndex = ref(0);

// Every file pages in the viewer; one it cannot show appears as its icon and name.
const viewerFile = computed(() => (isOpen.value ? (props.files[viewerIndex.value] ?? null) : null));

const formatUploadedAt = (createdAt: string) => {
    const timezone = userStore.getTimezone;
    const at = dayjs(createdAt);

    return (timezone ? at.tz(timezone) : at).format('DD/MM/YY h:mmA');
};

/*
 * One retry per failing preview. It is hidden at once, so a broken one never
 * shows, and only comes back once the retry brings a working link.
 */
const hiddenIds = ref(new Set<FileId>());
const retriedIds = new Set<FileId>();
const urlsBeforeRefresh = new Map<FileId, string>();

/*
 * Preview links are fetched as each slide needs one and reused until they near their 30-minute
 * expiry. An image the API built a preview for (`preview_url`) arrives with the list instead.
 */
const PREVIEW_LINK_REUSE_MS = 25 * 60 * 1000;

const resolvedUrls = ref(new Map<FileId, string>());
const resolvedAt = new Map<FileId, number>();
const resolving = new Set<FileId>();
const refetchingIds = new Set<FileId>();

const usesListPreview = (file: FileRecord) => previewKind(file) === 'image' && !!file.preview_url;

const previewUrlFor = (file: FileRecord): string | null =>
    usesListPreview(file) ? file.preview_url : (resolvedUrls.value.get(file.id) ?? null);

const hide = (id: FileId) => {
    hiddenIds.value = new Set(hiddenIds.value).add(id);
};

const unhide = (id: FileId) => {
    const hidden = new Set(hiddenIds.value);

    hidden.delete(id);
    hiddenIds.value = hidden;
};

const forgetUrl = (id: FileId) => {
    const urls = new Map(resolvedUrls.value);

    urls.delete(id);
    resolvedUrls.value = urls;
    resolvedAt.delete(id);
};

const requestPreviewUrl = async (file: FileRecord): Promise<void> => {
    if (previewKind(file) === null || usesListPreview(file) || resolving.has(file.id)) return;

    // Unavailable for good (refused, or failed after its retry); only a retry in flight asks again.
    if (hiddenIds.value.has(file.id) && !refetchingIds.has(file.id)) return;

    if (resolvedUrls.value.has(file.id)) {
        if (Date.now() - (resolvedAt.get(file.id) ?? 0) < PREVIEW_LINK_REUSE_MS) return;

        // An aged link is dropped first, so the slide shows the loader rather than an expired page.
        forgetUrl(file.id);
    }

    resolving.add(file.id);

    let failed = false;

    try {
        const url = await props.resolvePreviewUrl(file);

        if (!isOpen.value) return;

        resolvedUrls.value = new Map(resolvedUrls.value).set(file.id, url);
        resolvedAt.set(file.id, Date.now());

        if (refetchingIds.delete(file.id)) unhide(file.id);
    } catch {
        failed = true;
    } finally {
        resolving.delete(file.id);
    }

    if (!failed || !isOpen.value) return;

    // A link request that fails is asked for once more, like a preview that fails to load.
    if (retriedIds.has(file.id)) {
        refetchingIds.delete(file.id);
        hide(file.id);

        return;
    }

    retriedIds.add(file.id);
    await requestPreviewUrl(file);
};

const REFRESH_BATCH_MS = 300;
let refreshTimer: ReturnType<typeof setTimeout> | undefined;

// List previews expire together, so failures close together share one refetch.
const requestRefresh = () => {
    refreshTimer ??= setTimeout(() => {
        refreshTimer = undefined;
        emit('refresh');
    }, REFRESH_BATCH_MS);
};

const onPreviewError = (file: FileRecord | undefined) => {
    if (!file || hiddenIds.value.has(file.id)) return;

    hide(file.id);

    if (retriedIds.has(file.id)) return;

    retriedIds.add(file.id);

    // A fetched preview link is simply fetched again; one from the list needs the list refetched.
    if (!usesListPreview(file)) {
        forgetUrl(file.id);
        refetchingIds.add(file.id);
        requestPreviewUrl(file);

        return;
    }

    urlsBeforeRefresh.set(file.id, file.preview_url);
    requestRefresh();
};

/** Refused by the page's Content-Security-Policy: no link will load, so no retry either. */
const onPreviewBlocked = (file: FileRecord) => {
    retriedIds.add(file.id);
    hide(file.id);
};

// A preview waits for a new URL across refetches, so one landing first (a page appended) cannot strand it.
watch(
    () => props.files,
    (files) => {
        if (!urlsBeforeRefresh.size) return;

        const hidden = new Set(hiddenIds.value);

        urlsBeforeRefresh.forEach((url, id) => {
            const file = files.find((item) => item.id === id);

            if (file && previewUrlFor(file) !== url) {
                hidden.delete(id);
                urlsBeforeRefresh.delete(id);
            }
        });

        hiddenIds.value = hidden;
    },
);

// The list changing while open (a refetch, an upload) keeps the viewer on its file, or the nearest one if it has gone.
watch(
    () => props.files,
    (files, previous) => {
        if (!isOpen.value || !files.length) return;

        const found = files.findIndex((file) => file.id === previous[viewerIndex.value]?.id);

        viewerIndex.value = found === -1 ? Math.min(viewerIndex.value, files.length - 1) : found;
    },
);

const reset = () => {
    hiddenIds.value = new Set();
    retriedIds.clear();
    urlsBeforeRefresh.clear();
    resolvedUrls.value = new Map();
    resolvedAt.clear();
    resolving.clear();
    refetchingIds.clear();
    clearTimeout(refreshTimer);
    refreshTimer = undefined;
};

watch(isOpen, (open) => {
    if (!open) reset();
});

// Two files from the end, so the next page is usually in before paging would wrap to the first file.
watch([isOpen, viewerIndex, () => props.files.length], ([open, index, length]) => {
    if (open && length && index >= length - 2) emit('load-more');
});

// A refetch that leaves nothing to show closes the viewer rather than leaving it blank.
watch(
    () => props.files.length,
    (length) => {
        if (!length) isOpen.value = false;
    },
);

/** Opens the viewer at a file, for a click on a row or card of the list behind. */
const openAt = (file: FileRecord) => {
    viewerIndex.value = Math.max(
        0,
        props.files.findIndex((item) => item.id === file.id),
    );
    isOpen.value = true;
};

onBeforeUnmount(() => clearTimeout(refreshTimer));

defineExpose({ openAt });
</script>
